<script setup lang="ts">
import { getAuth, fetchSignInMethodsForEmail } from "firebase/auth";
import { ref } from "vue";
import AccountAction from "@/components/AccountAction.vue";
import AppButton from "@/components/AppButton.vue";
import AppInputText from "@/components/AppInputText.vue";
import AppLink from "@/components/AppLink.vue";
import useFormValidation from "@/modules/useFormValidation";
import Config from "@/config/Config";

const auth = getAuth();

const {
    validateRequiredField,
    validateEmailField,
    errors,
    hasErrors
} = useFormValidation();

interface Props {
    username: string;
}

const props = defineProps<Props>();

const username = ref(props.username);
const inputMode = ref("password");
const message = ref("");
const resetPassword = ref(false);

const validateEmailInput = () => validateEmailField("email", "Email", username.value);

const validateAll = () => {
    validateEmailInput();
};

const emit = defineEmits(["createUser", "signIn", "recoverPassword", "cancel"]);

const handleSignIn = async () => {
    validateAll();
    if (hasErrors.value) return;

    fetchSignInMethodsForEmail(auth, username.value)
        .then((signInMethods) => {
            if (signInMethods.includes("password")) {
                emit("signIn", { username: username.value });
            } else if (signInMethods.length > 0) {
                message.value = "The account requires a password reset. Please click on the 'Recover password' link."
            } else {
                emit("createUser", { username: username.value });
            }
        })
        .catch(() => {
            message.value = "Error looking up account. Please try again.";
        });
};
</script>

<template>
    <AccountAction>
        <template #title>
            Sign in with email
        </template>
        <template #default>
            <div class="flex flex-col gap-4">
                <p v-show="message" class="text-critical-text text-sm">
                    {{ message }}
                </p>

                <AppInputText
                    id="sign-in-email"
                    name="email"
                    label="Email"
                    type="email"
                    v-model="username"
                    :required="true"
                    @blur="validateEmailInput"
                    @keyup="validateEmailInput"
                />
            </div>
        </template>
        <template #actions>
            <div class="flex justify-evenly items-center w-full">
                
                <AppButton class="w-full"
                    title="Next"
                    @click="handleSignIn"
                />
            </div>
        </template>
        <template #footer>
            <div class="flex flex-col sm:flex-row justify-center gap-8 mt-4">
                <AppLink
                    buttonType="Secondary"
                    @click="$emit('recoverPassword', username)"
                >Recover password</AppLink>

                <a
                    :href="`${Config.links.help}/${Config.links.signIn}`"
                    target="_blank"
                    class="text-center text-action text-sm border-0 bg-transparent cursor-pointer"
                >Having problems signing in?</a>
            </div>
        </template>
    </AccountAction>
</template>
